/**
 * Dashboard Page Component
 * 
 * Displays contract analysis and reference mapping dashboard with:
 * - Summary statistics (4 metrics: Product Types, Categories, Channels, Territories)
 * - Multi-level hierarchical filters (Category, Channel, Territory)
 * - Contract details table with pagination (10 rows per page)
 * - Add/Edit Filters modal for dynamic filter configuration
 * 
 * Features:
 * - Responsive grid layout: 2-column (filters sidebar + main content)
 * - Summary cards with icons and numeric values
 * - Advanced filtering with MultiLevelSelect component
 * - Paginated table with 9 columns displaying contract metadata
 * - Modal dialog for filter management (currently hidden - d-none class)
 * - Bootstrap styling with card-based UI
 * 
 * Data Flow:
 * - Summary metrics: 4 static cards (25, 125, 99, 10)
 * - Table data: 42 sample contract records with pagination
 * - Filter selections: Category, Channel, Territory (multi-level hierarchical)
 * 
 * @component
 * @returns {JSX.Element} Dashboard layout with summary metrics, filters, and contract table
 * 
 * @requires react
 * @requires react-bootstrap (Container, Row, Col, Button, Modal, Form, Card, Table, Pagination)
 * @requires lucide-react (Package, Layers, Store, Globe icons)
 * @requires components/common/MultiLevelSelect (Hierarchical dropdown component)
 */

import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Card,
  Table,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import { Package, Layers, Store, Globe } from "lucide-react"; // Lucide React icons for dashboard metrics
import { MultiLevelSelect } from '../common/MultiLevelSelect'; // Multi-level hierarchical select component
import HeaderLogo  from "../structure/headerlogo";

/**
 * Dashboard Component - Contract analysis and reference mapping interface
 * 
 * Renders:
 * - Page header with title and description
 * - Summary statistics cards (4 metrics with icons)
 * - Left sidebar: Multi-select filters for 8 dimensions
 * - Right main area: Summary metrics grid + Contract details table
 * - Modal: Filter management dialog (hidden by default)
 * 
 * Sample data includes 42 contract records with pagination (10 per page)
 * 
 * @component
 * @returns {JSX.Element} Complete dashboard UI
 */
export const Dashboard = () => {

  // ========== MODAL STATE ==========
  // Controls visibility of Add/Edit Filters modal
  const [showModal, setShowModal] = useState(false);
  
  // ========== FILTER STATE ==========
  // Stores selected values for hierarchical multi-select filters
  const [category, setCategory] = useState(""); // Selected category value
  const [channel, setChannel] = useState(""); // Selected channel value
  const [territory, setTerritory] = useState(""); // Selected territory value

  // ========== SUMMARY METRICS DATA ==========
  /**
   * Summary statistics displayed as cards with icons
   * Each metric shows a count and associated icon from lucide-react
   * 
   * Metrics:
   * - Unique Product Types: 25 (blue Package icon)
   * - Unique Categories: 125 (yellow Layers icon)
   * - Unique Channels: 99 (green Store icon)
   * - Unique Territories: 10 (red Globe icon)
   * 
   * @type {Array<Object>}
   * @property {string} title - Metric name displayed in card
   * @property {number} value - Count value
   * @property {JSX.Element} icon - Lucide React icon component
   */
  const summaryData = [
  { title: "Unique Product Type", value: 25, icon: <Package size={32} color="#4f8cff" /> },
  { title: "Unique Category", value: 125, icon: <Layers size={32} color="#f7b731" /> },
  { title: "Unique Channel", value: 99, icon: <Store size={32} color="#26de81" /> },
  { title: "Unique Territory", value: 10, icon: <Globe size={32} color="#eb3b5a" /> },
];

  // ========== TABLE DATA & PAGINATION ==========
  /**
   * Sample contract data for table display
   * Generates 42 records with alternating values for demo purposes
   * 
   * Each record includes:
   * - licensor: Content licensing company (e.g., "Viz Media")
   * - fileName: PDF file name with numeric suffix
   * - property: Intellectual property name
   * - productType: "Office" or "Bags" (alternating)
   * - category: "Sticky Notes" or "Pencil Case" (alternating)
   * - categoryRestriction: Access level (fixed "Allowed")
   * - channel: Distribution channel ("Amazon Storefront" or "Off-Price")
   * - territory: Geographic region (fixed "United States of America")
   * - gender: Target demographic (fixed "All")
   * 
   * @type {Array<Object>}
   */
  const allData = Array.from({ length: 42 }, (_, i) => ({
    licensor: "Viz Media",
    fileName: `Viz_Media_-_Jojo_${i + 1}.pdf`,
    property: "JOJO's Bizarre Adventure (Season1-5)",
    productType: i % 2 === 0 ? "Office" : "Bags",
    category: i % 2 === 0 ? "Sticky Notes" : "Pencil Case",
    categoryRestriction: "Allowed",
    channel: i % 2 === 0 ? "Amazon Storefront" : "Off-Price",
    territory: "United States of America",
    gender: "All",
  }));

  // ========== PAGINATION STATE ==========
  const [currentPage, setCurrentPage] = useState(1); // Current page number (1-indexed)
  const rowsPerPage = 10; // Fixed rows displayed per page

  // Calculate total pages from data length
  const totalPages = Math.ceil(allData.length / rowsPerPage);
  
  // Slice data based on current page
  const paginatedData = allData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // ========== PAGINATION HANDLER ==========
  /**
   * Update current page when user clicks pagination button
   * Validates page number is within range [1, totalPages]
   * 
   * @function
   * @param {number} pageNumber - Page number to navigate to
   */
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // ========== PAGINATION RENDERER ==========
  /**
   * Render Bootstrap Pagination component with all page numbers
   * 
   * Features:
   * - First/Previous buttons (disabled on page 1)
   * - Page number buttons (1 to totalPages)
   * - Next/Last buttons (disabled on last page)
   * - Current page highlighted with active state
   * 
   * @function
   * @returns {JSX.Element} Pagination component
   */
  const renderPagination = () => {
    const items = [];
    // Generate page number buttons
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return (
      <Pagination className="justify-content-end mt-3">
        <Pagination.First
          disabled={currentPage === 1}
          onClick={() => handlePageChange(1)}
        />
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        />
        {items}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        />
        <Pagination.Last
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        />
      </Pagination>
    );
  };

  // ========== MULTI-LEVEL FILTER OPTIONS ==========
  /**
   * Category filter with hierarchical structure
   * 
   * Structure:
   * - Electronics
   *   - Phones (Smartphones, Feature Phones)
   *   - Laptops
   * - Clothing (Men, Women)
   * 
   * Used by MultiLevelSelect component for nested category selection
   * 
   * @type {Array<Object>}
   * @property {string} id - Unique identifier
   * @property {string} label - Display label
   * @property {Array<Object>} children - Sub-options
   */
  const categoryOptions = [
  { id: 'cat1', label: 'Electronics', children: [
    { id: 'cat1-1', label: 'Phones', children: [
      { id: 'cat1-1-1', label: 'Smartphones' },
      { id: 'cat1-1-2', label: 'Feature Phones' }
    ] },
    { id: 'cat1-2', label: 'Laptops' }
  ] },
  { id: 'cat2', label: 'Clothing', children: [
    { id: 'cat2-1', label: 'Men' },
    { id: 'cat2-2', label: 'Women' }
  ] }
];

  /**
   * Channel filter with hierarchical structure
   * 
   * Structure:
   * - Online (Amazon, eBay)
   * - Retail (Walmart, Target)
   * 
   * @type {Array<Object>}
   */
  const channelOptions = [
  { id: 'ch1', label: 'Online', children: [
    { id: 'ch1-1', label: 'Amazon' },
    { id: 'ch1-2', label: 'eBay' }
  ] },
  { id: 'ch2', label: 'Retail', children: [
    { id: 'ch2-1', label: 'Walmart' },
    { id: 'ch2-2', label: 'Target' }
  ] }
];

  /**
   * Territory filter with hierarchical structure
   * 
   * Structure:
   * - North America (USA, Canada)
   * - Europe (Germany, France)
   * 
   * @type {Array<Object>}
   */
  const territoryOptions = [
  { id: 't1', label: 'North America', children: [
    { id: 't1-1', label: 'USA' },
    { id: 't1-2', label: 'Canada' }
  ] },
  { id: 't2', label: 'Europe', children: [
    { id: 't2-1', label: 'Germany' },
    { id: 't2-2', label: 'France' }
  ] }
];

  // ========== RENDER JSX ==========
  /**
   * Dashboard Layout:
   * - Main container with page header and title section
   * - Two-column grid layout:
   *   - Left (md={2}): Filter sidebar with 8 filter controls
   *   - Right (md={10}): Summary cards grid + Contract table
   * - Add/Edit Filters modal (hidden by default with d-none class)
   * 
   * Grid System: Bootstrap responsive layout
   * - Responsive breakpoints: md (768px and up)
   * - Filter sidebar: Fixed width column for dimensional filtering
   * - Main content: Flexible column for metrics and table display
   * 
   * Sections:
   * 1. Page Header: Title and error message container
   * 2. Dashboard Controls: Settings and modal trigger
   * 3. Filters Sidebar: Multi-select dropdowns and hierarchical filters
   * 4. Main Content Area:
   *    - Summary Metrics: 4 cards with icons and count values
   *    - Contract Table: Paginated data display (10 rows per page)
   * 5. Filter Management Modal: Configure dashboard filters
   * 
   * @returns {JSX.Element} Complete dashboard interface
   */
  return (

    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          {/* ========== PAGE HEADER ========== */}
          <div id="table_height">
            {/* Page title and subtitle section */}
            <div className="flex-grow-1" id="row_heading">
              {/* Main page title */}
              <div className="page-title">Contract Genie Analysis with Reference Mapping</div>
              {/* Page subtitle/description */}
              <p className="text-muted my-2 ff-secondary">
                Contract Genie Analysis with Reference Mapping
              </p>
            </div>
            {/* Error message container for API/validation errors */}
            <div id="app_err"></div>
          </div>

      {/* ========== DASHBOARD HEADER & CONTROLS ========== */}
      {/* Header row with optional settings dropdown/button */}
      <Row className="align-items-center mb-3">
        <Col className="text-end">
          {/* Add/Edit Filters button (hidden by d-none class, toggles showModal state) */}
          <Button
            className="me-2 d-none"
            onClick={() => setShowModal(true)}
          >
            + Add/Edit Filters
          </Button>
        </Col>
      </Row>

      {/* ========== MAIN CONTENT GRID ========== */}
      {/* Two-column layout: filters (2 cols) + main content (10 cols) */}
      <Row>
        {/* ========== LEFT SIDEBAR: FILTERS ========== */}
        {/* Filter panel with multi-select dropdowns for dimensional analysis */}
        <Col md={2} className="border-end1">
          {/* Filters section title */}
          <h6 className="mb-3">Filters</h6>

          {/* Licensor dropdown - Standard dropdown for company selection */}
          <Form.Group className="mb-3">
            <Form.Label>Licensor</Form.Label>
            <Form.Select>
              <option>All</option>
            </Form.Select>
          </Form.Group>

          {/* FileName dropdown - Standard dropdown for document selection */}
          <Form.Group className="mb-3">
            <Form.Label>FileName</Form.Label>
            <Form.Select>
              <option>All</option>
            </Form.Select>
          </Form.Group>

          {/* Property dropdown - Standard dropdown for property selection */}
          <Form.Group className="mb-3">
            <Form.Label>Property</Form.Label>
            <Form.Select>
              <option>All</option>
            </Form.Select>
          </Form.Group>

          {/* Product Type dropdown - Standard dropdown for product category */}
          <Form.Group className="mb-3">
            <Form.Label>Product Type</Form.Label>
            <Form.Select>
              <option>All</option>
            </Form.Select>
          </Form.Group>

          {/* Category filter - Hierarchical multi-level select component */}
          {/* Options: Electronics (Phones, Laptops) | Clothing (Men, Women) */}
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <MultiLevelSelect
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              placeholder="All"
              searchable
            />
          </Form.Group>

          {/* Channel filter - Hierarchical multi-level select component */}
          {/* Options: Online (Amazon, eBay) | Retail (Walmart, Target) */}
          <Form.Group className="mb-3">
            <Form.Label>Channel</Form.Label>
            <MultiLevelSelect
              options={channelOptions}
              value={channel}
              onChange={setChannel}
              placeholder="All"
              searchable
            />
          </Form.Group>

          {/* Territory filter - Hierarchical multi-level select component */}
          {/* Options: North America (USA, Canada) | Europe (Germany, France) */}
          <Form.Group className="mb-3">
            <Form.Label>Territory</Form.Label>
            <MultiLevelSelect
              options={territoryOptions}
              value={territory}
              onChange={setTerritory}
              placeholder="All"
              searchable
            />
          </Form.Group>

          {/* Gender dropdown - Standard dropdown for gender/demographic */}
          <Form.Group className="mb-3">
            <Form.Label>Gender</Form.Label>
            <Form.Select>
              <option>All</option>
            </Form.Select>
          </Form.Group>

          {/* APPLY FILTERS button - Submits all selected filters */}
          <Button variant="secondary" className="w-100 mb-2">
            APPLY FILTERS
          </Button>

          {/* CLEAR ALL button - Resets all filter selections */}
          <Button variant="link" className="w-100 text-decoration-none">
            CLEAR ALL
          </Button>
        </Col>

        {/* ========== RIGHT MAIN CONTENT ========== */}
        {/* Main content area with summary cards and contract table */}
        <Col md={10}>
          {/* ========== SUMMARY METRICS CARDS ========== */}
          {/* Row of 4 summary statistic cards with icons */}
          {/* Each card displays: title, numeric value, and associated icon */}
          <Row className="mb-4">
          {summaryData.map((item, idx) => (
            <Col md={3} key={idx}>
              {/* Individual summary metric card */}
              {/* Card styling: white background, subtle shadow, flexible height */}
              <Card className="text-center shadow-sm border-0" style={{ background: "#fff", minHeight: 80, padding: 0 }}>
                <Card.Body className="d-flex align-items-center justify-content-between py-2 px-2" style={{ minHeight: 60 }}>
                  {/* Left section: Title and count value */}
                  <div className="text-start">
                    {/* Metric display name (e.g., "Product Types", "Categories") */}
                    <Card.Title className="fs-6 text-secondary mb-1" style={{ fontSize: 14 }}>{item.title}</Card.Title>
                    {/* Numeric value for the metric (e.g., 25, 125, 99, 10) */}
                    <h2 className="fw-bold mb-0" style={{ color: "#222f3e", fontSize: 22 }}>{item.value}</h2>
                  </div>
                  {/* Right section: Icon from lucide-react library */}
                  <div className="ms-2">{item.icon}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

          {/* ========== CONTRACT DETAILS TABLE ========== */}
          {/* Card wrapper for the main data table with pagination */}
          <Card>
            <Card.Body>
              {/* Table section title */}
              <h5>ContractDetails</h5>

              {/* Row selector dropdown for pagination control */}
              {/* Options: Show 10, 20 entries (10 rows per page is default) */}
              <div className="d-flex justify-content-between mb-2">
                <Form.Select style={{ width: "150px" }}>
                  <option>Show 10 entries</option>
                  <option>Show 20 entries</option>
                </Form.Select>
              </div>

              {/* Responsive table container with horizontal scroll on small screens */}
              <div style={{ overflowX: "auto" }}>
                {/* Bootstrap Table with striped rows, borders, and hover effect */}
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ minWidth: "1000px" }}
                >
                  {/* ========== TABLE HEADER ========== */}
                  {/* 9 columns: Licensor, FileName, Property, ProductType, Category, etc. */}
                  <thead>
                    <tr>
                      <th>Licensor</th>
                      <th>FileName</th>
                      <th>Property</th>
                      <th>ProductType</th>
                      <th>Category</th>
                      <th>CategoryRestriction</th>
                      <th>Channel</th>
                      <th>Territory</th>
                      <th>Gender</th>
                    </tr>
                  </thead>

                  {/* ========== TABLE BODY ========== */}
                  {/* Paginated data rows (10 records per page, 42 total records) */}
                  <tbody>
                    {paginatedData.map((row, idx) => (
                      <tr key={idx}>
                        {/* Each row displays contract metadata */}
                        <td>{row.licensor}</td>
                        <td>{row.fileName}</td>
                        <td>{row.property}</td>
                        <td>{row.productType}</td>
                        <td>{row.category}</td>
                        <td>{row.categoryRestriction}</td>
                        <td>{row.channel}</td>
                        <td>{row.territory}</td>
                        <td>{row.gender}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination controls - rendered below table data */}
              {renderPagination()}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========== ADD/EDIT FILTERS MODAL ========== */}
      /**
       * Modal Dialog for managing dashboard filter configuration
       * 
       * Layout (Two-column):
       * - Left Panel (md={4}): List of existing filters with delete buttons
       * - Right Panel (md={8}): Form to edit filter properties and settings
       * 
       * Configurable Properties:
       * - Filter Type: Value-based filtering (dropdown)
       * - Filter Name: Display name for the filter control
       * - Dataset: Target data table for the filter (ContractDetailsWithMapping)
       * - Column: Target column in the dataset for filtering
       * - Pre-filter: Optional SQL condition for available filter values
       * - Pre-filter Checkbox: Enable/disable pre-filtering functionality
       * 
       * Modal Controls:
       * - Close button (X): Dismisses the modal without saving
       * - Cancel button: Closes modal without changes
       * - Save button: Persists filter configuration changes
       * 
       * Visibility: Controlled by showModal state (initially false, toggled by settings button)
       */
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        {/* Modal header with title and close button */}
        <Modal.Header closeButton>
          <Modal.Title>Add and edit filters</Modal.Title>
        </Modal.Header>

        {/* Modal body with two-column layout */}
        <Modal.Body>
          <Row>
            {/* ========== LEFT PANEL: FILTERS LIST ========== */}
            {/* List of existing filters with management options */}
            <Col md={4} className="border-end">
              <Form.Group>
                {/* List title */}
                <Form.Label>Filters List</Form.Label>
                {/* Bootstrap list group for filter items */}
                <ul className="list-group">
                  {/* Example filter item with delete button */}
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    {/* Filter name */}
                    Licensor
                    {/* Delete filter button (trash icon) */}
                    <Button variant="link" size="sm">🗑</Button>
                  </li>
                </ul>
              </Form.Group>
            </Col>

            {/* ========== RIGHT PANEL: FILTER CONFIGURATION FORM ========== */}
            {/* Form fields to configure selected filter properties */}
            <Col md={8}>
              <Form>
                {/* Filter Type Selection */}
                <Form.Group className="mb-3">
                  <Form.Label>Filter Type</Form.Label>
                  {/* Dropdown to select filter mechanism (Value-based filtering) */}
                  <Form.Select defaultValue="Value">
                    <option>Value</option>
                  </Form.Select>
                </Form.Group>

                {/* Filter Display Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Filter Name</Form.Label>
                  {/* Text input for the human-readable filter label */}
                  <Form.Control type="text" defaultValue="Licensor" />
                </Form.Group>

                {/* Target Dataset Selection */}
                <Form.Group className="mb-3">
                  <Form.Label>Dataset</Form.Label>
                  {/* Dropdown to select which data table to filter */}
                  <Form.Select defaultValue="ContractDetailsWithMapping">
                    <option>ContractDetailsWithMapping</option>
                  </Form.Select>
                </Form.Group>

                {/* Target Column Selection */}
                <Form.Group className="mb-3">
                  <Form.Label>Column</Form.Label>
                  {/* Dropdown to select which column in the dataset to apply filter to */}
                  <Form.Select defaultValue="Licensor">
                    <option>Licensor</option>
                  </Form.Select>
                </Form.Group>

                {/* Pre-filter Available Values Toggle */}
                {/* Checkbox to enable/disable pre-filtering of available values */}
                <Form.Check
                  type="checkbox"
                  label="Pre-filter available values"
                  defaultChecked
                  className="mb-3"
                />

                {/* Pre-filter Condition Input */}
                <Form.Group>
                  <Form.Label>Pre-filter</Form.Label>
                  {/* Text input for SQL-like condition to pre-filter available values */}
                  {/* Example: Licensor <> 'Kodansha Limited' */}
                  <Form.Control
                    type="text"
                    defaultValue="Licensor <> 'Kodansha Limited'"
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>

        {/* Modal footer with action buttons */}
        <Modal.Footer>
          {/* Cancel button - closes modal without saving changes */}
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          {/* Save button - persists filter configuration changes */}
          <Button variant="primary">Save</Button>
        </Modal.Footer>
      </Modal>
    
            
            </div>
      </div>
    </div>

    
  );
}